import React, { createContext, useContext, useState, useEffect } from 'react'

interface Theme {
    mode: 'light' | 'dark'
    colors: {
        primary: string
        secondary: string
        background: string
        surface: string
        text: {
            primary: string
            secondary: string
        }
        error: string
        warning: string
        success: string
    }
}

interface ThemeContextType {
    theme: Theme
    toggleTheme: () => void
}

const lightTheme: Theme = {
    mode: 'light',
    colors: {
        primary: '#6366f1',
        secondary: '#8b5cf6',
        background: '#ffffff',
        surface: '#f8fafc',
        text: {
            primary: '#0f172a',
            secondary: '#475569',
        },
        error: '#ef4444',
        warning: '#f59e0b',
        success: '#10b981',
    },
}

const darkTheme: Theme = {
    mode: 'dark',
    colors: {
        primary: '#8b5cf6',
        secondary: '#a855f7',
        background: '#0f0f23',
        surface: '#1a1a2e',
        text: {
            primary: '#f1f5f9',
            secondary: '#94a3b8',
        },
        error: '#f87171',
        warning: '#fbbf24',
        success: '#34d399',
    },
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [theme, setTheme] = useState<Theme>(() => {
        // 로컬스토리지에서 저장된 테마 확인
        if (typeof window !== 'undefined') {
            const savedTheme = localStorage.getItem('theme')
            if (savedTheme === 'light') {
                return lightTheme
            }
            if (savedTheme === 'dark') {
                return darkTheme
            }
        }
        // 저장된 테마가 없으면 다크 모드를 기본값으로
        return darkTheme
    })

    useEffect(() => {
        // 테마 변경 시 로컬스토리지에 저장
        if (typeof window !== 'undefined') {
            localStorage.setItem('theme', theme.mode)
            document.documentElement.setAttribute('data-theme', theme.mode)
        }
    }, [theme.mode])

    const toggleTheme = () => {
        setTheme(prev => (prev.mode === 'light' ? darkTheme : lightTheme))
    }

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
} 